import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/** The seven deployment locales (Language enum). `ar` renders RTL. */
const LANGUAGES = ["en", "ar", "de", "fr", "it", "pt-PT", "pt-BR"] as const;
import { useParams, useSearchParams } from "react-router-dom";

import {
  getContent,
  REGIONS,
  searchCitations,
  submitContent,
  type SubmitResult,
} from "@/api/authoring";
import { useResolvedPermissions } from "@/auth/permissions";
import { useTenantScope } from "@/auth/tenant";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";
import { safeHttpUrl } from "@/util";

/**
 * Content detail + org-override editor (phase2-admin-ui slice).
 *
 * Renders one insight's (region, language) body with its citations. "Edit as
 * org override" seeds a JSON draft of a minimal ADR-0042-shaped insight file
 * for the chosen (region, lang); Submit opens the MR writing
 * `content/clients/{org}/insight-{id}.yaml` (ADR-0013 merge semantics — the
 * org's (region, lang) wins, everything else falls through to base).
 */
export function ContentDetailPage(): JSX.Element {
  const { insightId = "" } = useParams();
  const [params] = useSearchParams();
  const { org, pinned } = useTenantScope(params.get("org") ?? "");
  // docs#239 item 8: the override editor and "Submit as MR" need catalog:author,
  // which the submit route enforces. Without it the page is read-only.
  const permissions = useResolvedPermissions();
  const canAuthor =
    permissions.isReady && !permissions.isError && permissions.has("catalog:author");

  const [region, setRegion] = useState("EU");
  const [lang, setLang] = useState("en");
  const [draft, setDraft] = useState<string | null>(null);
  const [submitOrgInput, setSubmitOrgInput] = useState(org);
  const submitOrg = pinned ? org : submitOrgInput;

  // Citations are managed as a chip set OUTSIDE the raw JSON draft (which holds only
  // content_by_region) and merged in at submit — so the picker doesn't fight the JSON,
  // and the ADR-0006 ≥1-citation rule (the loader enforces it server-side) is surfaced
  // as a submit guard here.
  const [citations, setCitations] = useState<string[]>([]);
  const [citationQuery, setCitationQuery] = useState("");
  const rtl = lang.toLowerCase() === "ar";

  // Translation status for the language tabs: the authored-language set is per-REGION
  // (not per-language), so remember it from any successful fetch and reset when the
  // region changes — switching to a not-yet-translated language 404s and would
  // otherwise drop the indicators.
  const [availableLangs, setAvailableLangs] = useState<string[]>([]);

  const canonical =
    insightId.startsWith("#") || insightId.includes(":") ? insightId : `#${insightId}`;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["authoring-content", canonical, region, lang, org],
    queryFn: () => getContent(canonical, region, lang, org || undefined),
    retry: false,
  });

  useEffect(() => {
    if (data?.available_languages) setAvailableLangs(data.available_languages);
  }, [data]);

  const submission = useMutation<SubmitResult, Error, Record<string, unknown>>({
    mutationFn: (body) => submitContent(submitOrg, body),
  });

  // The citation picker — search the governed corpus (only once the author types a
  // couple of characters, to keep the query set small).
  const picker = useQuery({
    queryKey: ["citation-search", citationQuery],
    queryFn: () => searchCitations(citationQuery, 10),
    enabled: draft !== null && citationQuery.trim().length >= 2,
    retry: false,
  });

  const seedDraft = (): void => {
    setCitations((data?.citations ?? []).map((c) => c.id));
    setDraft(
      JSON.stringify(
        {
          insight: canonical,
          content_by_region: {
            [region]: {
              [lang]: {
                title: data?.content.title ?? null,
                body: data?.content.body ?? "",
              },
            },
          },
        },
        null,
        2,
      ),
    );
  };

  const addCitation = (id: string): void => {
    setCitations((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setCitationQuery("");
  };

  const parsedDraft = ((): Record<string, unknown> | null => {
    if (draft === null) return null;
    try {
      return JSON.parse(draft) as Record<string, unknown>;
    } catch {
      return null;
    }
  })();

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("authoring.content_title"), to: "/authoring/content" },
        { label: canonical },
      ]}
    >
      <Title order={1}>{canonical}</Title>
      <Group mt="md" mb="xs">
        <Select
          label={t("authoring.region")}
          data={[...REGIONS]}
          value={region}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) {
              setRegion(v);
              setAvailableLangs([]); // authored-language set is per-region
            }
          }}
        />
      </Group>
      {/* Translation tabs: a ✓ on a language means it is authored for this region;
          a dimmed one is not translated yet. Selecting it loads that language. */}
      <Tabs value={lang} onChange={(v) => v !== null && setLang(v)} mb="md">
        <Tabs.List>
          {LANGUAGES.map((code) => {
            const authored = availableLangs.includes(code);
            return (
              <Tabs.Tab
                key={code}
                value={code}
                rightSection={
                  authored ? (
                    <Badge size="xs" color="green" variant="light" circle aria-label="authored">
                      ✓
                    </Badge>
                  ) : undefined
                }
              >
                {authored ? (
                  code
                ) : (
                  <Text span c="dimmed">
                    {code}
                  </Text>
                )}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
      </Tabs>
      {isPending ? (
        <Text c="dimmed">{t("authoring.content_loading")}</Text>
      ) : isError ? (
        <Alert color="yellow" title={t("authoring.content_pair_missing")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : (
        <Card withBorder>
          <Stack gap="xs">
            {data.content.title !== null && (
              <Title order={3} dir={rtl ? "rtl" : "ltr"}>
                {data.content.title}
              </Title>
            )}
            <Text dir={rtl ? "rtl" : "ltr"} style={{ whiteSpace: "pre-wrap" }}>
              {data.content.body}
            </Text>
            {data.citations.length > 0 && (
              <Text size="sm" c="dimmed">
                {t("authoring.citations_label")}:{" "}
                {data.citations.map((c) => `${c.id} (${c.title}, ${c.year})`).join("; ")}
              </Text>
            )}
          </Stack>
        </Card>
      )}
      <Stack mt="lg">
        {!canAuthor ? null : draft === null ? (
          <Group>
            <Button variant="light" onClick={seedDraft} disabled={data === undefined}>
              {t("authoring.edit_as_override")}
            </Button>
          </Group>
        ) : (
          <>
            <Textarea
              label={t("authoring.override_draft_label")}
              description={t("authoring.override_draft_desc")}
              autosize
              minRows={10}
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              styles={{ input: { fontFamily: "monospace" } }}
            />
            {parsedDraft === null && <Alert color="red">{t("authoring.draft_invalid_json")}</Alert>}

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t("authoring.citations_edit_label")}
              </Text>
              {citations.length === 0 ? (
                <Text size="sm" c="red">
                  {t("authoring.citations_required")}
                </Text>
              ) : (
                <Group gap="xs">
                  {citations.map((id) => (
                    <Badge
                      key={id}
                      variant="light"
                      rightSection={
                        <Anchor
                          component="button"
                          type="button"
                          aria-label={`${t("authoring.citations_remove")} ${id}`}
                          onClick={() => setCitations((prev) => prev.filter((c) => c !== id))}
                        >
                          ×
                        </Anchor>
                      }
                    >
                      {id}
                    </Badge>
                  ))}
                </Group>
              )}
              <TextInput
                placeholder={t("authoring.citations_add_placeholder")}
                value={citationQuery}
                onChange={(e) => setCitationQuery(e.currentTarget.value)}
              />
              {picker.data !== undefined && picker.data.length > 0 && (
                <Stack gap={4}>
                  {picker.data.map((c) => (
                    <Anchor
                      key={c.id}
                      component="button"
                      type="button"
                      ta="left"
                      onClick={() => addCitation(c.id)}
                    >
                      {c.id} — {c.title} ({c.year})
                    </Anchor>
                  ))}
                </Stack>
              )}
            </Stack>

            <Group>
              {!pinned && (
                <TextInput
                  placeholder={t("authoring.org_placeholder")}
                  aria-label={t("authoring.submit_org")}
                  value={submitOrg}
                  onChange={(e) => setSubmitOrgInput(e.currentTarget.value)}
                />
              )}
              <Button
                disabled={parsedDraft === null || submitOrg === "" || citations.length === 0}
                loading={submission.isPending}
                onClick={() => {
                  if (parsedDraft !== null) submission.mutate({ ...parsedDraft, citations });
                }}
              >
                {t("authoring.submit_mr")}
              </Button>
            </Group>
            {submission.data !== undefined && (
              <Alert color="green" title={t("authoring.mr_opened")}>
                <Anchor href={safeHttpUrl(submission.data.mr_url)} target="_blank" rel="noreferrer">
                  {submission.data.mr_url}
                </Anchor>
              </Alert>
            )}
            {submission.isError && (
              <Alert color="red" title={t("authoring.error_title")}>
                {submission.error.message}
              </Alert>
            )}
          </>
        )}
      </Stack>
    </AppLayout>
  );
}
